import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { AuthContext, API } from "../App";
import { toast } from "sonner";
import { Search, ArrowLeft, Download, Clock, Users, ChefHat, Loader2, Filter } from "lucide-react";
import { Footer } from "../components/Footer";

const CATEGORY_ICONS = {
  viande: "🥩", volaille: "🍗", poisson: "🐟", pates: "🍝",
  vege: "🥗", desserts: "🍰", breuvages: "🍹", rapide: "⚡", autre: "🍽️", all: "🔥"
};

export default function Discover() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [recipes, setRecipes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [userCategories, setUserCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [showCategoryPicker, setShowCategoryPicker] = useState(null);

  useEffect(() => {
    fetchData();
  }, [selectedCategory]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [recipesRes, catsRes, userCatsRes] = await Promise.all([
        axios.get(`${API}/discover/recipes${selectedCategory !== "all" ? `?category=${selectedCategory}` : ""}`, { withCredentials: true }),
        axios.get(`${API}/discover/categories`, { withCredentials: true }),
        axios.get(`${API}/categories`, { withCredentials: true }),
      ]);
      setRecipes(recipesRes.data);
      setCategories(catsRes.data);
      setUserCategories(userCatsRes.data);
    } catch (err) {
      toast.error("Erreur lors du chargement des recettes vedettes");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (recipe) => {
    setShowCategoryPicker(recipe);
  };

  const confirmDownload = async (recipe, categoryId) => {
    try {
      setDownloading(recipe.cache_id);
      setShowCategoryPicker(null);
      const res = await axios.post(
        `${API}/discover/download/${recipe.cache_id}?category_id=${categoryId}`,
        {},
        { withCredentials: true }
      );
      toast.success(res.data.message || "Recette ajoutée!");
      fetchData();
    } catch (err) {
      const msg = err.response?.data?.detail || "Erreur lors du téléchargement";
      toast.error(msg);
    } finally {
      setDownloading(null);
    }
  };

  const filtered = recipes.filter(r =>
    !searchQuery || r.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (r.creator_name || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div data-testid="discover-page" style={{ minHeight: "100vh", background: "#1A1A1A", color: "#F5F1E8" }}>
      {/* Header */}
      <div style={{
        position: "sticky", top: 0, zIndex: 50, padding: "1rem",
        background: "rgba(26,26,26,0.95)", backdropFilter: "blur(12px)",
        borderBottom: "1px solid rgba(135,169,107,0.2)"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "0.75rem" }}>
          <button
            data-testid="discover-back-btn"
            onClick={() => navigate("/dashboard")}
            style={{ background: "none", border: "none", color: "#87A96B", cursor: "pointer", padding: "0.25rem" }}
          >
            <ArrowLeft size={24} />
          </button>
          <h1 style={{ fontSize: "1.4rem", fontWeight: 800, color: "#F5F1E8", margin: 0 }}>
            Découvrir
          </h1>
          <span style={{
            fontSize: "0.7rem", background: "rgba(135,169,107,0.2)", color: "#87A96B",
            padding: "0.15rem 0.5rem", borderRadius: "999px", fontWeight: 600
          }}>
            {recipes.length} recettes
          </span>
        </div>

        {/* Search */}
        <div style={{ position: "relative", marginBottom: "0.75rem" }}>
          <Search size={16} style={{ position: "absolute", left: "0.75rem", top: "50%", transform: "translateY(-50%)", color: "#888" }} />
          <input
            data-testid="discover-search"
            type="text"
            placeholder="Rechercher une recette ou un créateur..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: "100%", padding: "0.6rem 0.6rem 0.6rem 2.2rem",
              background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: "10px", color: "#F5F1E8", fontSize: "0.85rem", outline: "none",
              boxSizing: "border-box"
            }}
          />
        </div>

        {/* Category pills */}
        <div style={{ display: "flex", gap: "0.4rem", overflowX: "auto", paddingBottom: "0.25rem" }}
          className="hide-scrollbar">
          <button
            data-testid="discover-cat-all"
            onClick={() => setSelectedCategory("all")}
            style={{
              flexShrink: 0, padding: "0.35rem 0.75rem", borderRadius: "999px", border: "none",
              fontSize: "0.75rem", fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap",
              background: selectedCategory === "all" ? "#87A96B" : "rgba(255,255,255,0.08)",
              color: selectedCategory === "all" ? "#FFF" : "#AAA",
              transition: "all 0.2s"
            }}
          >
            🔥 Toutes
          </button>
          {categories.map(cat => (
            <button
              key={cat.id}
              data-testid={`discover-cat-${cat.id}`}
              onClick={() => setSelectedCategory(cat.id)}
              style={{
                flexShrink: 0, padding: "0.35rem 0.75rem", borderRadius: "999px", border: "none",
                fontSize: "0.75rem", fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap",
                background: selectedCategory === cat.id ? "#87A96B" : "rgba(255,255,255,0.08)",
                color: selectedCategory === cat.id ? "#FFF" : "#AAA",
                transition: "all 0.2s"
              }}
            >
              {cat.icon} {cat.name}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: "1rem" }}>
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "3rem" }}>
            <Loader2 className="animate-spin" size={32} style={{ color: "#87A96B" }} />
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: "3rem 1rem", color: "#888" }}>
            <p style={{ fontSize: "1.2rem", marginBottom: "0.5rem" }}>Aucune recette trouvée</p>
            <p style={{ fontSize: "0.85rem" }}>Les recettes vedettes apparaissent ici quand les utilisateurs extraient des recettes.</p>
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: "0.75rem" }}>
            {filtered.map(recipe => (
              <div
                key={recipe.cache_id}
                data-testid={`discover-recipe-${recipe.cache_id}`}
                style={{
                  background: "rgba(255,255,255,0.05)", borderRadius: "14px",
                  overflow: "hidden", border: "1px solid rgba(255,255,255,0.08)",
                  transition: "transform 0.2s, border-color 0.2s",
                  cursor: "pointer"
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.borderColor = "rgba(135,169,107,0.4)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)"; }}
              >
                {/* Image - clic ouvre la vidéo dans le navigateur externe */}
                <div
                  style={{ position: "relative", paddingTop: "100%", overflow: "hidden", cursor: "pointer" }}
                  onClick={() => {
                    if (recipe.source_url) {
                      window.open(recipe.source_url, '_blank', 'noopener,noreferrer');
                    }
                  }}
                >
                    <img
                      src={recipe.image.startsWith("/") ? `${API.replace("/api", "")}/api${recipe.image}` : recipe.image}
                      alt={recipe.title}
                      style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", objectFit: "cover" }}
                      onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1546548970-71785318a17b?w=400&h=300&fit=crop"; }}
                    />
                  {/* Play overlay */}
                  <div style={{
                    position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
                    width: "36px", height: "36px", borderRadius: "50%",
                    background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    opacity: 0.85, transition: "opacity 0.2s"
                  }}>
                    <span style={{ color: "#FFF", fontSize: "1rem", marginLeft: "2px" }}>▶</span>
                  </div>
                  {/* Download badge */}
                  <div style={{
                    position: "absolute", top: "0.4rem", right: "0.4rem",
                    background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)",
                    borderRadius: "8px", padding: "0.2rem 0.45rem",
                    display: "flex", alignItems: "center", gap: "0.25rem",
                    fontSize: "0.65rem", color: "#87A96B", fontWeight: 700
                  }}>
                    <Download size={10} />
                    {recipe.download_count}
                  </div>
                  {/* Category badge */}
                  <div style={{
                    position: "absolute", bottom: "0.4rem", left: "0.4rem",
                    background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)",
                    borderRadius: "6px", padding: "0.15rem 0.4rem",
                    fontSize: "0.6rem", color: "#DDD"
                  }}>
                    {CATEGORY_ICONS[recipe.category] || "🍽️"} {recipe.category_label}
                  </div>
                </div>

                {/* Info */}
                <div style={{ padding: "0.6rem" }}>
                  <h3
                    onClick={() => recipe.source_url && window.open(recipe.source_url, '_blank', 'noopener,noreferrer')}
                    style={{
                      fontSize: "0.8rem", fontWeight: 700, color: "#F5F1E8",
                      margin: "0 0 0.25rem", lineHeight: 1.3, cursor: "pointer",
                      display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden"
                    }}
                  >
                    {recipe.title}
                  </h3>
                  
                  {recipe.creator_name && (
                    <p style={{ fontSize: "0.65rem", color: "#87A96B", margin: "0 0 0.35rem", fontWeight: 600 }}>
                      Par {recipe.creator_name}
                    </p>
                  )}

                  <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap", marginBottom: "0.4rem" }}>
                    {recipe.prep_time && (
                      <span style={{ fontSize: "0.6rem", color: "#999", display: "flex", alignItems: "center", gap: "0.15rem" }}>
                        <Clock size={9} /> {recipe.prep_time}
                      </span>
                    )}
                    {recipe.servings && (
                      <span style={{ fontSize: "0.6rem", color: "#999", display: "flex", alignItems: "center", gap: "0.15rem" }}>
                        <Users size={9} /> {recipe.servings}
                      </span>
                    )}
                  </div>

                  <button
                    data-testid={`discover-download-${recipe.cache_id}`}
                    onClick={(e) => { e.stopPropagation(); handleDownload(recipe); }}
                    disabled={downloading === recipe.cache_id}
                    style={{
                      width: "100%", padding: "0.4rem", border: "none", borderRadius: "8px",
                      background: downloading === recipe.cache_id ? "#666" : "#87A96B",
                      color: "#FFF", fontSize: "0.7rem", fontWeight: 700, cursor: "pointer",
                      display: "flex", alignItems: "center", justifyContent: "center", gap: "0.3rem",
                      transition: "background 0.2s"
                    }}
                  >
                    {downloading === recipe.cache_id ? (
                      <Loader2 className="animate-spin" size={12} />
                    ) : (
                      <>
                        <Download size={12} />
                        Ajouter (1 crédit)
                      </>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Category Picker Modal */}
      {showCategoryPicker && (
        <div
          data-testid="category-picker-overlay"
          onClick={() => setShowCategoryPicker(null)}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)",
            zIndex: 100, display: "flex", alignItems: "flex-end", justifyContent: "center"
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: "#2A2A2A", borderRadius: "16px 16px 0 0", width: "100%",
              maxWidth: "500px", maxHeight: "60vh", overflow: "auto", padding: "1.25rem"
            }}
          >
            <h3 style={{ color: "#F5F1E8", fontSize: "1rem", fontWeight: 700, marginBottom: "0.75rem" }}>
              Dans quelle catégorie?
            </h3>
            <p style={{ color: "#999", fontSize: "0.8rem", marginBottom: "1rem" }}>
              Choisissez où sauvegarder « {showCategoryPicker.title} »
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
              {userCategories.map(cat => (
                <button
                  key={cat.id}
                  data-testid={`picker-cat-${cat.id}`}
                  onClick={() => confirmDownload(showCategoryPicker, cat.id)}
                  style={{
                    padding: "0.7rem 1rem", background: "rgba(255,255,255,0.06)",
                    border: "1px solid rgba(255,255,255,0.1)", borderRadius: "10px",
                    color: "#F5F1E8", fontSize: "0.85rem", fontWeight: 600,
                    cursor: "pointer", textAlign: "left", transition: "background 0.2s"
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = "rgba(135,169,107,0.2)"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.06)"; }}
                >
                  {cat.name}
                </button>
              ))}
              {userCategories.length === 0 && (
                <p style={{ color: "#999", fontSize: "0.8rem", textAlign: "center", padding: "1rem" }}>
                  Aucune catégorie. Créez-en une dans votre dashboard d'abord.
                </p>
              )}
            </div>
            <button
              onClick={() => setShowCategoryPicker(null)}
              style={{
                marginTop: "0.75rem", width: "100%", padding: "0.6rem", background: "rgba(255,255,255,0.08)",
                border: "none", borderRadius: "10px", color: "#999", fontSize: "0.8rem", cursor: "pointer"
              }}
            >
              Annuler
            </button>
          </div>
        </div>
      )}

      <style>{`
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
      <Footer />
    </div>
  );
}
